import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText, Box, SideDrawer, withStyles } from 'vopay';
import { useBlockingLoader } from 'shared';
import { Permissions, PermissionValues } from 'shared/permissions';
import { Skeleton } from 'UI';
import useSubAccountPermissions from '@/hooks/useApi/useSubAccountPermissions';
import useSubAccountPermissionLabels from '@/hooks/useSubAccountPermissionLabels';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { useToast } from '@/providers/Toast.provider';
import adaptAccountPermissions from './adaptAccountPermissions';
import styles from './AccountPermissionsDrawer.module.scss';

// The account portal's host for the shared permissions editor: it fetches, adapts and label-bakes the
// sub-account's permissions, lays the shared parts out in the drawer, and saves.

const cn = withStyles(styles);

const SKELETON_ROW_COUNT = 6;

type AccountPermissionsDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  accountID?: string;
  accountName?: string;
  currency?: string;
};

export default function AccountPermissionsDrawer({
  isOpen,
  onClose,
  accountID,
  accountName,
  currency,
}: AccountPermissionsDrawerProps) {
  const { fetchingPermissions, permissions, fetchSubAccountPermissions, saveSubAccountPermissions } =
    useSubAccountPermissions();

  const labelResolvers = useSubAccountPermissionLabels();
  const alertDialog = useAlertDialog();
  const blockingLoader = useBlockingLoader();
  const toast = useToast();

  const {
    response: { Country },
  } = useClientData();

  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.Permissions' });

  const sections = useMemo(() => adaptAccountPermissions(permissions, labelResolvers), [permissions, labelResolvers]);

  useEffect(() => {
    if (!accountID || !isOpen) return;

    const loadPermissions = async () => {
      try {
        await fetchSubAccountPermissions({ SubaccountID: accountID });
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : t('Errors.fetchPermissions', { defaultValue: 'Failed to load permissions. Please try again.' });

        alertDialog.error(t('Errors.title', { defaultValue: 'Error' }), errorMessage);
      }
    };

    loadPermissions();
  }, [accountID, isOpen]);

  const handleSave = async (values: PermissionValues) => {
    if (!accountID) return;

    blockingLoader.show();

    try {
      await saveSubAccountPermissions({ SubaccountID: accountID, ...values });
      onClose();
      toast.success(t('Success.permissionsUpdated', { defaultValue: 'Permissions updated successfully' }));
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t('Errors.savePermissions', { defaultValue: 'Failed to save permissions. Please try again.' });

      alertDialog.error(t('Errors.title', { defaultValue: 'Error' }), errorMessage);
    } finally {
      blockingLoader.hide();
    }
  };

  return (
    <Permissions
      sections={sections}
      currency={currency || (Country === 'US' ? 'USD' : 'CAD')}
      formID="accountPermissionForm"
      onSave={handleSave}
    >
      <SideDrawer
        isOpen={isOpen}
        onClose={onClose}
        headerClassName={cn('whiteBackground')}
        footerClassName={cn('whiteBackground')}
        title={
          <div className={cn('header')}>
            <span>{t('Drawer.title', { defaultValue: 'Edit Permissions' })}</span>

            {accountName && (
              <BodyText weight="md" className={cn('accountName')}>
                {accountName}
              </BodyText>
            )}
          </div>
        }
        headerFullWidthContent={
          <div className={cn('searchWrapper')}>
            <Permissions.Search />
          </div>
        }
        footer={
          <div className={cn('footer')}>
            <Permissions.EnableAllButton disabled={fetchingPermissions} />
            <Permissions.SaveButton disabled={fetchingPermissions} />
          </div>
        }
      >
        {fetchingPermissions ? (
          <Box flx="col" gap="sm">
            {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
              <Skeleton key={index} height="56px" />
            ))}
          </Box>
        ) : (
          <Permissions.Sections />
        )}
      </SideDrawer>
    </Permissions>
  );
}
